import React, {useEffect, useRef, useState} from 'react';

import {Button} from 'primereact/button';
import {ProgressSpinner} from 'primereact/progressspinner';
import {SelectButton} from 'primereact/selectbutton';
import {Tooltip} from 'primereact/tooltip';

import './ChartWrapperStyle.scss';
import {DatasetChartWrapperProps} from '../interfaces';
import {defaultFromDateTime, defaultToDateTime} from '../utils';

import {addToDate, getDiffSeconds, truncateDate} from "utils/time-utils";
import CustomToggleButton from '../../../basic/custom-toggle-button/CustomToggleButton';
import {useResizeDetector} from 'react-resize-detector';
import {extractNumber} from '../../product-template-table/utils/utils';
import CalendarWrapper from '../../../basic/calendar-wrapper/CalendarWrapper';
import {useGetDataInfoRange} from 'api/api-hooks/datasets-hooks';
import {dialogIcon} from '../icons';
import {Dialog} from 'primereact/dialog';

export default function ChartWrapper(props: DatasetChartWrapperProps) {
    const [loaded, setLoaded] = useState(false);
    const { width, height, ref } = useResizeDetector();
    const { height: height2, ref: ref2 } = useResizeDetector();

    const [chartDialog, setChartDialog] = useState(false);

    const [from, setFrom] = useState(
        defaultFromDateTime({
            loadConf: {
                alignUnit: 'MINUTES',
                lag: 6,
                lagUnit: 'HOURS',
                lead: 0,
                leadUnit: 'HOURS',
                ...props.loadConf,
            },
            referenceTime: props.referenceTime,
        })
    );
    const [to, setTo] = useState(
        defaultToDateTime({
            loadConf: {
                alignUnit: 'MINUTES',
                lag: 6,
                lagUnit: 'HOURS',
                lead: 0,
                leadUnit: 'HOURS',
                ...props.loadConf,
            },
            referenceTime: props.referenceTime,
        })
    );

    useEffect(() => {
        let dticksAmount;
        if (props.timeAxisStepSeconds) {
            dticksAmount = (to.getMilliseconds() - from.getMilliseconds()) / (props.timeAxisStepSeconds * 1000);
                if (dticksAmount <= props.maxDTicks!!) {
                    props.setDticks!(props.timeAxisStepSeconds * 1000);
                } else {
                    props.setDticks!(undefined);
                }
        }
    }, [from, to]);

    const chartDialogHandler = () => {
        if (props.chartDialogOpen) {
            return;
        }
        setChartDialog((prevState) => !prevState);
    };

    const dialogButton = {
        title: 'Chart in Dialog',
        name: 'Chart in Dialog',
        icon: dialogIcon,
        click: chartDialogHandler,
    };

    const modeBarButtons = [...props.chartConfig?.modeBarButtonsToAdd!!];
    modeBarButtons.unshift(dialogButton);

    const config = { ...props.chartConfig, modeBarButtonsToAdd: modeBarButtons };

    const [changedTimeOffset, setChangedTimeOffset] = useState<boolean>(false);
    const [selectedTimeOffset, setSelectedTimeOffset] = useState<string | undefined>(undefined);
    const offsetOptions = [
        { label: 'H', value: 'HOURS' },
        { label: 'D', value: 'DAYS' },
        { label: 'W', value: 'WEEKS' },
        { label: 'M', value: 'MONTHS' },
    ];

    const requestingTime = useRef<{ from: Date | undefined; to: Date | undefined; loading: boolean }>({
        from: undefined,
        to: undefined,
        loading: false,
    });

    const [refresh, setRefresh] = useState<{ updateFrequency: number; turned: boolean }>({
        updateFrequency: props.updateFrequency && !props.reportMode || props.reportMode === undefined ? props.updateFrequency ?? 5 : 0,
        turned: !!props.updateFrequency,
    });

    const presentDate = useRef<Date>();

    const {
        data: traces,
        isFetching: isLoading,
        refetch,
    } = useGetDataInfoRange(
        props.identifiers as string[],
        {
            uniqueKey: props.uniqueKey,
            timeStart: from.toISOString(),
            timeEnd: to.toISOString(),
            usePromiseHandler: (res) => {
                requestingTime.current.to = new Date();
                requestingTime.current.loading = false;
                if (!res.error) {
                    return props.promiseHandler(res);
                } else {
                    console.log(res.error.message);
                    throw res.error;
                }
            },
        },
        {
            enabled: from && to && props.identifiers.length > 0,
            staleTime: 1000,
        }
    );

    useEffect(() => {
        if (traces && !isLoading) {
            setLoaded(true);
        }
        if (isLoading && !requestingTime.current.loading) {
            requestingTime.current.from = new Date();
            requestingTime.current.loading = true;
        }
    }, [traces, isLoading]);

    useEffect(() => {
        if (refresh.turned && refresh.updateFrequency && refresh.updateFrequency > 0 && presentDate && presentDate.current) {
            const interval = setInterval(() => {
                let tempDate = truncateDate(new Date(), 'MINUTES');
                if (presentDate && presentDate.current && presentDate.current?.toISOString().localeCompare(truncateDate(new Date(), 'MINUTES').toISOString()) !== 0) {
                    let minutesToAdd = getDiffSeconds(tempDate, presentDate.current!) / 60;
                    if (minutesToAdd > 0) {
                        addTimeToBothDates(minutesToAdd);
                        presentDate.current = truncateDate(new Date(), 'MINUTES');
                    }
                } else {
                    refetch();
                }
            }, refresh.updateFrequency * 1000);
            return () => clearInterval(interval);
        }
    }, [refresh.turned, refresh.updateFrequency, presentDate.current]);

    useEffect(() => {
        if (selectedTimeOffset) {
            setFrom(addToDate(new Date(to), -1, selectedTimeOffset));
        } else {
            if (from && changedTimeOffset) {
                setFrom(defaultFromDateTime({ loadConf: props.loadConf, referenceTime: props.referenceTime }));
            }
        }
    }, [selectedTimeOffset]);

    const addTimeToBothDates = (minutesToAdd: number) => {
        setFrom((oldFrom) => addToDate(oldFrom, minutesToAdd, 'MINUTES'));
        setTo((oldTo) => addToDate(oldTo, minutesToAdd, 'MINUTES'));
    };

    const onRefresh = () => {
        if (from && to && props.identifiers) {
            refetch();
        }
    };

    const setFromDateHandler = (date: Date) => {
        if (refresh.turned) {
            setRefresh({ ...refresh, turned: false });
        }
        setChangedTimeOffset(false);
        setFrom(date);
        setSelectedTimeOffset(undefined);
    };

    const setToDateHandler = (date: Date) => {
        if (refresh.turned) {
            setRefresh({ ...refresh, turned: false });
        }
        setChangedTimeOffset(false);
        setTo(date);
        setSelectedTimeOffset(undefined);
    };

    const onSelectButtonChange = (e: any) => {
        if (refresh.turned) {
            setRefresh({ ...refresh, turned: false });
        }
        setSelectedTimeOffset(e.value);
        setChangedTimeOffset(true);
    };

    const syncPresentData = () => {
        presentDate.current = truncateDate(new Date(), 'MINUTES');
    };

    const totalDataPoints = () => {
        if (traces) {
            return traces.map((t: any) => t.x?.length).reduce((c: any, a: any) => c! + a!, 0);
        }

        return 0;
    };

    const printRequestingTime = () => {
        if (requestingTime.current.from && requestingTime.current.to) {
            let time = requestingTime.current.to.getTime() - requestingTime.current.from.getTime();
            if (time > 1000) {
                return (time / 1000).toFixed(1) + 's';
            } else {
                return time.toFixed(0) + 'ms';
            }
        } else {
            return '-';
        }
    };

    const getChartHeight = () => {
        if (props.height != null) {
            return extractNumber(props.height);
        } else if (height && height2 && height >= 0 && height2 >= 0 && props.dynamicHeight) {
            return height - height2 - 4;
        } else {
            return 400;
        }
    };

    const getChartWidth = () => {
        if (props.width != null) {
            return extractNumber(props.width);
        } else {
            return width;
        }
    };

    if (props.reportMode && !props.width) {
        return <h3 style={{ color: 'red' }}>Width property is required</h3>;
    }

    return (
        <div
            ref={ref}
            style={{
                overflow: 'hidden',
                height: '100%',
                width: props.reportMode ? props.width : undefined,
            }}
            id={props.uniqueKey}
        >
            <div ref={ref2} className='flex align-items-center p-mx-2 justify-content-between non-printable'>
                <div className='flex align-items-center p-mx-2 '>
                    {props.showDateSelection && (
                        <>
                            <CalendarWrapper id='from' value={from} onChange={setFromDateHandler} className='p-mr-2' debounceTimeInMs={300} />
                            <CalendarWrapper id='to' value={to} onChange={setToDateHandler} className='p-mr-2' debounceTimeInMs={300} />
                        </>
                    )}

                    <div className='flex gap-2 align-items-center'>
                        <div style={{ flexWrap: 'nowrap', whiteSpace: 'nowrap' }}>
                            <SelectButton value={selectedTimeOffset} options={offsetOptions} onChange={onSelectButtonChange} className={'select-button-class'} />
                        </div>

                        {props.datasetChart !== 'pie' && (
                            <span className='flex align-items-center'>
                                Total data points:
                                {isLoading ? (
                                    <span>
                                        <ProgressSpinner style={{ width: '2.5rem', height: '2.5rem' }} strokeWidth='4' fill='var(--surface-ground)' animationDuration='.5s' />
                                    </span>
                                ) : (
                                    <>
                                        <Tooltip target={'.time-spent-requesting'} showDelay={1000} />
                                        <span className='time-spent-requesting' data-pr-tooltip={printRequestingTime()}>
                                            {totalDataPoints()}
                                        </span>
                                    </>
                                )}
                            </span>
                        )}
                    </div>
                </div>
                <div className={'flex align-items-center'}>
                    {!props.hideRefresh && (
                        <div>
                            <Button icon='pi pi-refresh' type='button' loading={isLoading} onClick={onRefresh} className='p-mr-2 p-button-sm' />
                        </div>
                    )}

                    <CustomToggleButton
                        active={refresh.turned}
                        setActive={(val) => {
                            syncPresentData();
                            setRefresh({ ...refresh, turned: val });
                        }}
                    />
                </div>
            </div>
            {!loaded ? (
                <div className='flex justify-content-center p-mb-4'>
                    <ProgressSpinner style={{ width: '20rem', height: '20rem' }} strokeWidth='3' animationDuration='2s' />
                </div>
            ) : (
                props.children!({
                    width: extractNumber(getChartWidth()),
                    height: getChartHeight(),
                    data: traces || [],
                    axesConfig: props.axesConfig ? props.axesConfig(traces || []) : undefined,
                    chartConfig: config as Partial<Plotly.Config>,
                    chartDialogOpen: chartDialog,
                })
            )}
            {chartDialog && (
                <Dialog
                    visible={chartDialog}
                    style={{ width: 'min(100vw, 1000px)', height: '600px' }}
                    modal={false}
                    resizable={true}
                    draggable={true}
                    maximizable
                    onHide={chartDialogHandler}
                >
                    <ChartWrapper {...props} dynamicHeight={true} chartDialogOpen={true} />
                </Dialog>
            )}
        </div>
    );
}
